import React from 'react'
import { MapPin, Phone, Mail, Clock, Facebook, Instagram, Linkedin, BookOpen, Twitter, Youtube, Music } from 'lucide-react'
import { NAVIGATION_ITEMS } from '../../utils/constants'
import { useSectionContent } from '../../hooks/useContent'
import { useFooterLogoSettings } from '../../hooks/useFooterLogoSettings'
import { useFooterSettings } from '../../hooks/useFooterSettings'
import { useSocialMediaLinks } from '../../hooks/useSocialMediaLinks'

const Footer: React.FC = () => {
  const { getContent, loading } = useSectionContent('contact')
  const { footerLogoSettings, loading: logoLoading } = useFooterLogoSettings()
  const { footerSettings, loading: footerLoading } = useFooterSettings()
  const { activeSocialLinks, socialSettings, loading: socialLoading } = useSocialMediaLinks()
  
  // Get contact information from database with fallbacks
  const contactInfo = {
    address: getContent('address', '123 Language Street\nKathmandu, Nepal'),
    phone: getContent('phone', '+977-1-234-5678'),
    email: getContent('email', 'info@nwlc.com'),
    hours: getContent('hours', 'Monday - Friday: 9:00 AM - 6:00 PM\nSaturday: 10:00 AM - 4:00 PM\nSunday: Closed')
  }

  // Icon mapping for social media
  const getIcon = (iconName: string) => {
    const iconMap: Record<string, React.ComponentType<any>> = {
      Facebook,
      Twitter,
      Instagram,
      Linkedin,
      Youtube,
      Music // TikTok uses Music icon
    }
    const IconComponent = iconMap[iconName] || Facebook
    return IconComponent
  }
  return (
    <footer className="bg-gray-900 text-white">
      <div className="container py-12">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
          {/* About Section */}
          <div className="col-span-1 lg:col-span-2">
            <div className="flex items-center space-x-3 mb-6">
              {footerLogoSettings.footer_show_logo_image && footerLogoSettings.footer_logo_url ? (
                <div className="relative">
                  <img
                    src={footerLogoSettings.footer_logo_url}
                    alt="Footer Logo"
                    style={{
                      width: `${footerLogoSettings.footer_logo_width}px`,
                      height: `${footerLogoSettings.footer_logo_height}px`
                    }}
                    className="object-contain"
                  />
                </div>
              ) : (
                <div className="w-10 h-10 bg-crimson rounded-lg flex items-center justify-center">
                  <BookOpen className="w-6 h-6 text-white" />
                </div>
              )}
            </div>
            {footerSettings.show_company_info && (
              <p className="text-gray-300 mb-6 leading-relaxed max-w-md">
                {footerSettings.company_description}
              </p>
            )}
            {/* Social Media Icons - Only show if footer display is enabled */}
            {socialSettings.show_social_links_footer && (
              <div className="flex space-x-4">
                {!socialLoading && activeSocialLinks.length > 0 ? (
                  // Show admin-managed social links when available
                  activeSocialLinks.map((link) => {
                    const IconComponent = getIcon(link.icon_name)
                    return (
                      <a
                        key={link.id}
                        href={link.url}
                        className="w-10 h-10 bg-gray-800 rounded-lg flex items-center justify-center text-gray-400 hover:text-white hover:bg-crimson transition-colors duration-200"
                        aria-label={link.platform_name}
                        title={link.platform_name}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        <IconComponent className="w-5 h-5" />
                      </a>
                    )
                  })
                ) : (
                  // Show default social links when no admin links are configured
                  <>
                    <a
                      href="https://facebook.com"
                      className="w-10 h-10 bg-gray-800 rounded-lg flex items-center justify-center text-gray-400 hover:text-white hover:bg-crimson transition-colors duration-200"
                      aria-label="Facebook"
                      title="Facebook"
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      <Facebook className="w-5 h-5" />
                    </a>
                    <a
                      href="https://twitter.com"
                      className="w-10 h-10 bg-gray-800 rounded-lg flex items-center justify-center text-gray-400 hover:text-white hover:bg-crimson transition-colors duration-200"
                      aria-label="Twitter"
                      title="Twitter"
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      <Twitter className="w-5 h-5" />
                    </a>
                    <a
                      href="https://instagram.com"
                      className="w-10 h-10 bg-gray-800 rounded-lg flex items-center justify-center text-gray-400 hover:text-white hover:bg-crimson transition-colors duration-200"
                      aria-label="Instagram"
                      title="Instagram"
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      <Instagram className="w-5 h-5" />
                    </a>
                    <a
                      href="https://linkedin.com"
                      className="w-10 h-10 bg-gray-800 rounded-lg flex items-center justify-center text-gray-400 hover:text-white hover:bg-crimson transition-colors duration-200"
                      aria-label="LinkedIn"
                      title="LinkedIn"
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      <Linkedin className="w-5 h-5" />
                    </a>
                  </>
                )}
              </div>
            )}
          </div>

          {/* Quick Links */}
          <div>
            <h4 className="text-lg font-semibold mb-6 text-white">Quick Links</h4>
            <ul className="space-y-3">
              {NAVIGATION_ITEMS.map((item) => (
                <li key={item.name}>
                  <a 
                    href={item.href} 
                    className="text-gray-300 hover:text-white transition-colors duration-200"
                  >
                    {item.name}
                  </a>
                </li>
              ))}
            </ul>
          </div>

          {/* Contact Info */}
          {footerSettings.show_contact_info && (
            <div>
              <h4 className="text-lg font-semibold mb-6 text-white">Contact Us</h4>
              <div className="space-y-4">
                <div className="flex items-start space-x-3">
                  <MapPin className="w-5 h-5 text-crimson mt-0.5 flex-shrink-0" />
                  <p className={`text-gray-300 text-sm whitespace-pre-line ${loading ? 'opacity-0' : ''}`}>{contactInfo.address}</p>
                </div>
                
                <div className="flex items-center space-x-3">
                  <Phone className="w-5 h-5 text-crimson flex-shrink-0" />
                  <p className={`text-gray-300 text-sm ${loading ? 'opacity-0' : ''}`}>{contactInfo.phone}</p>
                </div>
                
                <div className="flex items-center space-x-3">
                  <Mail className="w-5 h-5 text-crimson flex-shrink-0" />
                  <p className={`text-gray-300 text-sm ${loading ? 'opacity-0' : ''}`}>{contactInfo.email}</p>
                </div>
                
                <div className="flex items-center space-x-3">
                  <Clock className="w-5 h-5 text-crimson flex-shrink-0" />
                  <p className={`text-gray-300 text-sm whitespace-pre-line ${loading ? 'opacity-0' : ''}`}>{contactInfo.hours}</p>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Bottom Section with SVG */}
        <div className="border-t border-gray-800 mt-12 pt-8">
          <div className="flex flex-col md:flex-row justify-between items-center mb-6">
            <p className="text-gray-400 text-sm">
              {footerSettings.copyright_text}
            </p>
            <a
              href="https://brandspire.com.np/"
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center space-x-2 text-gray-500 hover:text-gray-300 transition-colors duration-200 text-xs mt-4 md:mt-0"
            >
              <img
                src="https://favicon.im/brandspire.com.np"
                alt="Brandspire Logo"
                className="w-4 h-4"
                onError={(e) => {
                  // Fallback to another favicon service
                  const target = e.currentTarget;
                  if (target.src.includes('favicon.im')) {
                    target.src = 'https://icons.duckduckgo.com/ip3/brandspire.com.np.ico';
                  } else {
                    target.style.display = 'none';
                  }
                }}
              />
              <span>Developed by Brandspire Creatives</span>
            </a>
          </div>
          
        </div>
      </div>
    </footer>
  )
}

export default Footer